import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  Button,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { 
  getPaymentHistory, 
  formatAmount, 
  getPaymentStatusText, 
  getPaymentStatusColor 
} from '../../services/paymentService';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // 获取支付历史
  const fetchPayments = async (pageNum = 0, limit = 10, status = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getPaymentHistory({
        page: pageNum + 1, // API使用1-based页码
        limit,
        status
      });

      if (response.success) {
        setPayments(response.data.payments);
        setTotalCount(response.data.pagination.total);
      } else {
        setError('获取支付历史失败');
      }
    } catch (err) {
      setError(err.message || '获取支付历史失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    fetchPayments(page, rowsPerPage, statusFilter);
  }, [page, rowsPerPage, statusFilter]);

  // 处理页面变化
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 处理每页行数变化
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 处理状态筛选
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // 处理菜单操作
  const handleMenuClick = (event, payment) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedPayment(null);
  };

  // 查看支付详情
  const handleViewDetails = () => {
    if (selectedPayment) {
      // 这里可以实现查看详情的逻辑
      console.log('View payment details:', selectedPayment);
      // 可以打开一个详情对话框或导航到详情页面
    }
    handleMenuClose();
  };

  // 格式化日期
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && payments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          支付历史
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>状态筛选</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              input={<OutlinedInput label="状态筛选" />}
            >
              <MenuItem value="">全部</MenuItem>
              <MenuItem value="pending">待支付</MenuItem>
              <MenuItem value="succeeded">支付成功</MenuItem>
              <MenuItem value="failed">支付失败</MenuItem>
              <MenuItem value="refunded">已退款</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />}
            onClick={() => fetchPayments(page, rowsPerPage, statusFilter)}
          >
            刷新
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>专业人员</TableCell>
                  <TableCell>金额</TableCell>
                  <TableCell>状态</TableCell>
                  <TableCell>时间</TableCell>
                  <TableCell>描述</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {payment.professionalId?.name || '未知用户'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {payment.professionalId?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {formatAmount(payment.amount, payment.currency)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={getPaymentStatusText(payment.status)}
                        color={getPaymentStatusColor(payment.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(payment.createdAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {payment.description || 'Coffee Chat 会话'}
                      </Typography>
                      {payment.sessionId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="textSecondary">
                            {payment.sessionId.duration || 60} 分钟
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    
                    <TableCell align="right">
                      <Tooltip title="更多操作">
                        <IconButton 
                          onClick={(e) => handleMenuClick(e, payment)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {payments.length === 0 && !loading && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              py={4}
              color="text.secondary"
            >
              <ReceiptIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">暂无支付记录</Typography>
              <Typography variant="body2">
                您还没有任何支付记录
              </Typography>
            </Box>
          )}

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="每页行数:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} 共 ${count !== -1 ? count : `超过 ${to}`} 条`
            }
          />
        </CardContent>
      </Card>

      {/* 操作菜单 */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <ReceiptIcon sx={{ mr: 1 }} />
          查看详情
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PaymentHistory; 